import { SearchableSelect } from "@/components/ui/searchable-select";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { usePagedList, unwrapPage } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Radio,
  Plug,
  Plus,
  RefreshCw,
  Search,
  Code,
  Key,
  Globe,
  Webhook,
  Play,
  Copy,
  Check,
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Trash2,
  Settings,
  Shuffle,
  Users,
  ShieldAlert,
  ArrowRight,
  Info,
  Calendar,
  Layers,
  PhoneCall,
  Share2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { LeadSourceConnection, LeadInboxItem, MasterLookupRecord } from "@/types";
import { Link } from "react-router-dom";
import { dateLocale, withClock } from "@/lib/dateTime";

type Tab = "connections" | "inbox";

const CONNECTION_TYPE_META: Record<
  string,
  { label: string; icon: React.ElementType; color: string; desc: string; badge: string }
> = {
  WEB_FORM: {
    label: "Website Form",
    icon: Globe,
    color: "text-primary bg-primary/10 border-primary/20",
    desc: "Add a ready-made form to your website or landing pages",
    badge: "Form",
  },
  API_KEY: {
    label: "Software link",
    icon: Key,
    color: "text-accent-foreground bg-primary/10 border-primary/20",
    desc: "Your own software sends leads using a secret code",
    badge: "Software",
  },
  WEBHOOK: {
    label: "Lead link",
    icon: Webhook,
    color: "text-warning bg-warning/10 border-warning/20",
    desc: "Any other app sends leads to a private link, checked with a secret code",
    badge: "Link",
  },
  INDIAMART: {
    label: "IndiaMART",
    icon: Radio,
    color: "text-success bg-success/10 border-success/20",
    desc: "Picks up new inquiries every 5 mins, plus instant delivery",
    badge: "IndiaMART",
  },
  JUSTDIAL: {
    label: "JustDial",
    icon: PhoneCall,
    color: "text-warning bg-warning/10 border-warning/20",
    desc: "New JustDial inquiries arrive instantly",
    badge: "JustDial",
  },
  META_LEADS: {
    label: "Facebook & Instagram Ads",
    icon: Share2,
    color: "text-info bg-info/10 border-info/20",
    desc: "Instant leads from Facebook & Instagram lead forms",
    badge: "Social Ads",
  },
  GOOGLE_ADS: {
    label: "Google Ads",
    icon: Search,
    color: "text-danger bg-danger/10 border-danger/20",
    desc: "Instant leads from Google Ads lead forms",
    badge: "Google Ads",
  },
};

export function LeadSourcesPage() {
  const { success, error, info } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>("connections");

  // Connections state
  const [connections, setConnections] = useState<LeadSourceConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);

  // Lookups & users for wizard
  const [assignableReps, setAssignableReps] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const [leadSources, setLeadSources] = useState<MasterLookupRecord[]>([]);

  // Dialogs
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [snippetModalOpen, setSnippetModalOpen] = useState(false);
  const [selectedConnectionForSnippet, setSelectedConnectionForSnippet] = useState<LeadSourceConnection | null>(null);
  const [snippetData, setSnippetData] = useState<{ html: string; script: string; endpoint: string } | null>(null);
  const [snippetLoading, setSnippetLoading] = useState(false);

  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [keyReveal, setKeyReveal] = useState<{ title: string; apiKey?: string; webhookUrl: string; secret?: string } | null>(null);

  const [itemDetailModalOpen, setItemDetailModalOpen] = useState(false);
  const [activeInboxItem, setActiveInboxItem] = useState<LeadInboxItem | null>(null);
  const [loadingItemDetail, setLoadingItemDetail] = useState(false);

  // Delete & Rotate Confirmation Dialogs
  const [deleteConnectionTarget, setDeleteConnectionTarget] = useState<LeadSourceConnection | null>(null);
  const [deletingConnection, setDeletingConnection] = useState(false);
  const activeDeleteTarget = React.useRef<LeadSourceConnection | null>(null);
  if (deleteConnectionTarget) activeDeleteTarget.current = deleteConnectionTarget;

  const [rotateKeyTarget, setRotateKeyTarget] = useState<LeadSourceConnection | null>(null);
  const [rotatingKey, setRotatingKey] = useState(false);
  const activeRotateTarget = React.useRef<LeadSourceConnection | null>(null);
  if (rotateKeyTarget) activeRotateTarget.current = rotateKeyTarget;

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [testingConnectionId, setTestingConnectionId] = useState<string | null>(null);
  const [syncingIndiaMartId, setSyncingIndiaMartId] = useState<string | null>(null);

  // Inbox Filters
  const [filterConnId, setFilterConnId] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Create Connection Form
  const [formData, setFormData] = useState({
    name: "",
    type: "WEB_FORM",
    source_code: "WEBSITE",
    routing_mode: "ROUND_ROBIN",
    default_owner_id: "",
    round_robin_user_ids: [] as string[],
    // config fields
    allowed_origins: "",
    redirect_url: "",
    glusr_crm_key: "",
    enable_polling: true,
    poll_frequency_minutes: 5,
    secret: "",
    google_key: "",
  });

  const loadConnections = useCallback(async () => {
    setLoadingConnections(true);
    try {
      const res = await api.get("/api/LeadSource/Connections");
      setConnections(unwrapArray<LeadSourceConnection>(res));
    } catch (err: any) {
      error("Error Loading Connections", err?.message || "Failed to fetch lead connections");
    } finally {
      setLoadingConnections(false);
    }
  }, [error]);

  useEffect(() => {
    loadConnections();

    api
      .get("/api/User/Assignable?for=lead")
      .then((res) => setAssignableReps(unwrapArray(res)))
      .catch(() => {});

    api
      .get("/api/MasterLookup/ByCategory/LEAD_SOURCE")
      .then((res) => setLeadSources(unwrapArray(res)))
      .catch(() => {});
  }, [loadConnections]);

  // Paged Inbox List
  const inboxList = usePagedList<LeadInboxItem>({
    deps: [filterConnId, filterStatus, searchQuery],
    debounceMs: 300,
    load: async (page) => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", "25");
      if (filterConnId) params.set("connection_id", filterConnId);
      if (filterStatus) params.set("status", filterStatus);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());

      const res = await api.get(`/api/LeadSource/Inbox?${params.toString()}`);
      return unwrapPage<LeadInboxItem>(res);
    },
  });

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleToggleActive = async (conn: LeadSourceConnection) => {
    try {
      await api.patch(`/api/LeadSource/Connections/${conn.id}`, { is_active: !conn.is_active });
      setConnections((prev) =>
        prev.map((c) => (c.id === conn.id ? { ...c, is_active: !c.is_active } : c))
      );
      success("Status Updated", `Connection ${conn.is_active ? "paused" : "activated"}`);
    } catch (err: any) {
      error("Update Failed", err?.message || "Could not toggle connection status");
    }
  };

  const handleTestConnection = async (conn: LeadSourceConnection) => {
    setTestingConnectionId(conn.id);
    try {
      const res: any = await api.post(`/api/LeadSource/Connections/${conn.id}/Test`);
      const data = unwrapObject<any>(res);
      success(
        "Test Lead Received",
        `Sample lead received (ref ${data?.inbox_id?.slice(0, 8)}...). It will be added within 60 seconds.`
      );
      loadConnections();
      inboxList.reload();
    } catch (err: any) {
      error("Test Lead Failed", err?.message || "Could not send a test lead");
    } finally {
      setTestingConnectionId(null);
    }
  };

  const handleSyncIndiaMart = async (conn: LeadSourceConnection) => {
    setSyncingIndiaMartId(conn.id);
    try {
      const res: any = await api.post(`/api/LeadSource/Connections/${conn.id}/SyncIndiaMart`);
      const data = unwrapObject<any>(res);
      success("IndiaMART Sync Started", `Checked ${data?.leads_found || 0} inquiries from IndiaMART`);
      loadConnections();
      inboxList.reload();
    } catch (err: any) {
      error("Sync Failed", err?.message || "IndiaMART sync didn't work");
    } finally {
      setSyncingIndiaMartId(null);
    }
  };

  const handleOpenSnippet = async (conn: LeadSourceConnection) => {
    setSelectedConnectionForSnippet(conn);
    setSnippetLoading(true);
    setSnippetModalOpen(true);
    try {
      const res: any = await api.get(`/api/Intake/Form/${conn.public_key}/Snippet`);
      setSnippetData(unwrapObject<any>(res));
    } catch (err: any) {
      error("Form Code Error", err?.message || "Could not make the form code");
    } finally {
      setSnippetLoading(false);
    }
  };

  const handleConfirmRotateKey = async () => {
    if (!rotateKeyTarget) return;
    setRotatingKey(true);
    try {
      const res: any = await api.post(`/api/LeadSource/Connections/${rotateKeyTarget.id}/RotateKey`);
      const data = unwrapObject<any>(res);
      const origin = window.location.origin;
      const targetName = rotateKeyTarget.name;
      const targetPubKey = rotateKeyTarget.public_key;
      setRotateKeyTarget(null);
      setKeyReveal({
        title: `New Codes: ${targetName}`,
        apiKey: data?.api_key,
        secret: data?.webhook_secret,
        webhookUrl: `${origin}/api/Intake/Hook/${targetPubKey}`,
      });
      setApiKeyModalOpen(true);
      loadConnections();
      success("New Codes Made", "Make sure to copy the new secret code now.");
    } catch (err: any) {
      error("Couldn't Make New Codes", err?.message || "Failed to make new codes");
    } finally {
      setRotatingKey(false);
    }
  };

  const handleConfirmDeleteConnection = async () => {
    if (!deleteConnectionTarget) return;
    setDeletingConnection(true);
    try {
      await api.delete(`/api/LeadSource/Connections/${deleteConnectionTarget.id}`);
      success("Connection Deleted", deleteConnectionTarget.name);
      setDeleteConnectionTarget(null);
      loadConnections();
    } catch (err: any) {
      error("Delete Failed", err?.message || "Could not delete connection");
    } finally {
      setDeletingConnection(false);
    }
  };

  const handleCreateConnection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      error("Validation", "Connection name is required");
      return;
    }

    try {
      const config: Record<string, any> = {};
      if (formData.type === "WEB_FORM") {
        config.allowed_origins = formData.allowed_origins
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        config.redirect_url = formData.redirect_url.trim() || undefined;
      } else if (formData.type === "INDIAMART") {
        config.glusr_crm_key = formData.glusr_crm_key.trim();
        config.enable_polling = formData.enable_polling;
        config.poll_frequency_minutes = Number(formData.poll_frequency_minutes) || 5;
      } else if (formData.type === "GOOGLE_ADS") {
        config.google_key = formData.google_key.trim();
      }

      const routing: any = {
        mode: formData.routing_mode,
        default_owner_id: formData.default_owner_id || undefined,
        round_robin_user_ids: formData.routing_mode === "ROUND_ROBIN" ? formData.round_robin_user_ids : undefined,
      };

      const payload: any = {
        name: formData.name.trim(),
        type: formData.type,
        source_code: formData.source_code,
        config,
        routing,
      };

      if (formData.secret.trim()) {
        payload.secret = formData.secret.trim();
      }

      const res: any = await api.post("/api/LeadSource/Connections", payload);
      const data = unwrapObject<any>(res);
      const conn = data?.connection || data;
      const origin = window.location.origin;

      setCreateModalOpen(false);
      loadConnections();
      success("Connection Created", `${conn.name} is now ready.`);

      // If API key was returned or webhook has secret, show reveal modal
      if (data?.api_key || formData.type === "API_KEY" || formData.type === "WEBHOOK" || formData.type === "INDIAMART" || formData.type === "JUSTDIAL") {
        setKeyReveal({
          title: `Setup: ${conn.name}`,
          apiKey: data?.api_key,
          secret: formData.secret || undefined,
          webhookUrl:
            formData.type === "INDIAMART"
              ? `${origin}/api/Intake/IndiaMart/${conn.public_key}`
              : formData.type === "JUSTDIAL"
              ? `${origin}/api/Intake/JustDial/${conn.public_key}`
              : formData.type === "GOOGLE_ADS"
              ? `${origin}/api/Intake/Google/${conn.public_key}`
              : `${origin}/api/Intake/Hook/${conn.public_key}`,
        });
        setApiKeyModalOpen(true);
      }
    } catch (err: any) {
      error("Creation Failed", err?.message || "Could not create lead connection");
    }
  };

  const handleViewInboxDetail = async (item: LeadInboxItem) => {
    setActiveInboxItem(item);
    setItemDetailModalOpen(true);
    setLoadingItemDetail(true);
    try {
      const res: any = await api.get(`/api/LeadSource/Inbox/${item.id}`);
      setActiveInboxItem(unwrapObject<LeadInboxItem>(res));
    } catch (err: any) {
      error("Fetch Detail Error", err?.message);
    } finally {
      setLoadingItemDetail(false);
    }
  };

  const handleRetryInboxItem = async (itemId: string) => {
    try {
      await api.post(`/api/LeadSource/Inbox/${itemId}/Retry`);
      success("Retry Started", "It will be tried again right away");
      inboxList.reload();
      if (itemDetailModalOpen && activeInboxItem?.id === itemId) {
        handleViewInboxDetail(activeInboxItem);
      }
    } catch (err: any) {
      error("Retry Failed", err?.message || "Could not retry it");
    }
  };

  const handleSkipInboxItem = async (itemId: string) => {
    try {
      await api.post(`/api/LeadSource/Inbox/${itemId}/Skip`);
      success("Item Skipped", "Item marked as skipped");
      inboxList.reload();
      if (itemDetailModalOpen && activeInboxItem?.id === itemId) {
        handleViewInboxDetail(activeInboxItem);
      }
    } catch (err: any) {
      error("Skip Failed", err?.message || "Could not mark skipped");
    }
  };

  const handleBulkRetry = async () => {
    try {
      const res: any = await api.post("/api/LeadSource/Inbox/BulkRetry", { all_failed: true });
      const count = unwrapObject<any>(res)?.retried || 0;
      success("Retry Started", `${count} failed items will be tried again`);
      inboxList.reload();
    } catch (err: any) {
      error("Bulk Retry Failed", err?.message || "Could not retry failed items");
    }
  };

  // Aggregated totals
  const stats = useMemo(() => {
    const active = connections.filter((c) => c.is_active).length;
    const todayRecv = connections.reduce((acc, c) => acc + (Number(c.metrics?.today) || 0), 0);
    const todayProc = connections.reduce((acc, c) => acc + (Number(c.metrics?.processed_today) || 0), 0);
    const todayFail = connections.reduce((acc, c) => acc + (Number(c.metrics?.failed_today) || 0), 0);
    return { active, todayRecv, todayProc, todayFail };
  }, [connections]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <Radio className="size-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
              Lead Sources & Intake
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Connect lead channels (IndiaMART, JustDial, website forms, your own software, ads) to capture, de-duplicate and assign leads automatically.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === "connections" && (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setFormData({
                  name: "",
                  type: "WEB_FORM",
                  source_code: "WEBSITE",
                  routing_mode: "ROUND_ROBIN",
                  default_owner_id: "",
                  round_robin_user_ids: assignableReps.map((r) => r.id),
                  allowed_origins: "",
                  redirect_url: "",
                  glusr_crm_key: "",
                  enable_polling: true,
                  poll_frequency_minutes: 5,
                  secret: "",
                  google_key: "",
                });
                setCreateModalOpen(true);
              }}
              className="gap-1.5 shadow-sm font-semibold"
            >
              <Plus className="size-4" />
              <span>Connect Channel</span>
            </Button>
          )}

          {activeTab === "inbox" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleBulkRetry}
              className="gap-1.5 text-xs font-semibold text-warning border-warning/30 hover:bg-warning/10"
            >
              <RefreshCw className="size-3.5" />
              <span>Retry All Failed</span>
            </Button>
          )}
        </div>
      </div>

      {/* KPI Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-card border border-border/80 rounded-xl p-3.5 space-y-1 shadow-2xs">
          <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
            <span>Active Channels</span>
            <Plug className="size-3.5 text-primary" />
          </div>
          <div className="text-xl font-bold text-foreground">
            {stats.active} <span className="text-xs font-normal text-muted-foreground">/ {connections.length}</span>
          </div>
        </div>

        <div className="bg-card border border-border/80 rounded-xl p-3.5 space-y-1 shadow-2xs">
          <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
            <span>Inquiries Today</span>
            <Clock className="size-3.5 text-primary" />
          </div>
          <div className="text-xl font-bold text-foreground">{stats.todayRecv}</div>
        </div>

        <div className="bg-card border border-border/80 rounded-xl p-3.5 space-y-1 shadow-2xs">
          <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
            <span>Processed Today</span>
            <CheckCircle2 className="size-3.5 text-success" />
          </div>
          <div className="text-xl font-bold text-success">{stats.todayProc}</div>
        </div>

        <div className="bg-card border border-border/80 rounded-xl p-3.5 space-y-1 shadow-2xs">
          <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
            <span>Failed Leads</span>
            <AlertCircle className="size-3.5 text-danger" />
          </div>
          <div className={cn("text-xl font-bold", stats.todayFail > 0 ? "text-danger" : "text-muted-foreground")}>
            {stats.todayFail}
          </div>
        </div>
      </div>

      {/* Subtabs */}
      <div className="flex border-b border-border/80 gap-6 text-sm font-semibold">
        <button
          type="button"
          onClick={() => setActiveTab("connections")}
          className={cn(
            "pb-2.5 transition-colors relative flex items-center gap-2",
            activeTab === "connections"
              ? "text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Layers className="size-4" />
          <span>Configured Channels</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
            {connections.length}
          </Badge>
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab("inbox");
            inboxList.reload();
          }}
          className={cn(
            "pb-2.5 transition-colors relative flex items-center gap-2",
            activeTab === "inbox"
              ? "text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Clock className="size-4" />
          <span>Inquiry Log (Lead Inbox)</span>
          {inboxList.total > 0 && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
              {inboxList.total}
            </Badge>
          )}
        </button>
      </div>

      {/* TAB 1: CONNECTIONS */}
      {activeTab === "connections" && (
        <div className="space-y-4">
          {loadingConnections ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="h-44 bg-muted/40 animate-pulse rounded-2xl border" />
              ))}
            </div>
          ) : connections.length === 0 ? (
            <div className="bg-card border border-dashed rounded-2xl p-12 text-center max-w-lg mx-auto space-y-4">
              <div className="size-12 rounded-full bg-primary/10 text-primary mx-auto flex items-center justify-center">
                <Radio className="size-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground">No channels connected yet</h3>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  Set up your website contact form, connect IndiaMART or JustDial, or link your own software to begin capturing inbound prospects.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 shadow-sm font-semibold"
              >
                <Plus className="size-4" />
                <span>Connect Your First Channel</span>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {connections.map((conn) => {
                const meta = CONNECTION_TYPE_META[conn.type] || {
                  label: conn.type,
                  icon: Plug,
                  color: "text-foreground bg-muted",
                  desc: "External channel",
                  badge: conn.type,
                };
                const IconComponent = meta.icon;
                const isTesting = testingConnectionId === conn.id;
                const isSyncing = syncingIndiaMartId === conn.id;

                const hasError = Boolean(conn.last_error);
                const isDegraded = (conn.consecutive_failures || 0) >= 3;

                return (
                  <div
                    key={conn.id}
                    className={cn(
                      "bg-card border rounded-2xl p-4.5 flex flex-col justify-between transition-all hover:border-primary/40 hover:shadow-sm space-y-4",
                      isDegraded ? "border-danger/40 bg-danger/5" : hasError ? "border-warning/40" : "border-border/80"
                    )}
                  >
                    {/* Top Row */}
                    <div className="space-y-2.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className={cn("size-9 rounded-xl border flex items-center justify-center shrink-0", meta.color)}>
                            <IconComponent className="size-4.5" />
                          </div>
                          <div>
                            <div className="font-bold text-sm text-foreground flex items-center gap-2">
                              <span className="truncate max-w-[180px]">{conn.name}</span>
                            </div>
                            <span className="text-[11px] text-muted-foreground font-medium">{meta.label}</span>
                          </div>
                        </div>

                        <Switch
                          checked={conn.is_active}
                          onCheckedChange={() => handleToggleActive(conn)}
                          title={conn.is_active ? "Channel Active" : "Channel Paused"}
                        />
                      </div>

                      {/* Routing info badge */}
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <Badge variant="outline" className="text-[10px] font-normal py-0 h-5 gap-1">
                          <Shuffle className="size-2.5 opacity-70" />
                          <span>
                            {conn.routing?.mode === "ROUND_ROBIN"
                              ? "Round-Robin Reps"
                              : conn.routing?.mode === "TEAM_ROUND_ROBIN"
                              ? "Team Round-Robin"
                              : conn.routing?.mode === "FIXED_USER"
                              ? "Fixed Rep"
                              : "Unassigned Pool"}
                          </span>
                        </Badge>

                        {conn.source_code && (
                          <Badge variant="secondary" className="text-[10px] py-0 h-5 font-normal">
                            {conn.source_code}
                          </Badge>
                        )}

                        {conn.is_active ? (
                          <Badge className="bg-success/10 text-success border-success/20 text-[10px] py-0 h-5 font-medium">
                            Operational
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] py-0 h-5 text-muted-foreground">
                            Paused
                          </Badge>
                        )}
                      </div>

                      {/* Error Banner if any */}
                      {conn.last_error && (
                        <div className="bg-danger/10 border border-danger/30 rounded-lg p-2 text-[11px] text-danger flex items-start gap-1.5">
                          <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
                          <div className="truncate" title={conn.last_error}>
                            {conn.last_error}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Middle Stats Box */}
                    <div className="grid grid-cols-3 gap-2 bg-muted/40 rounded-xl p-2.5 text-center text-xs">
                      <div>
                        <div className="text-[10px] text-muted-foreground uppercase font-semibold">Today</div>
                        <div className="font-bold text-foreground mt-0.5">{conn.metrics?.today || 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground uppercase font-semibold">Processed</div>
                        <div className="font-bold text-success mt-0.5">
                          {conn.metrics?.processed_today || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground uppercase font-semibold">Total</div>
                        <div className="font-bold text-foreground mt-0.5">{conn.metrics?.total || 0}</div>
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-between gap-1 pt-1 border-t border-border/60">
                      <div className="flex items-center gap-1">
                        {conn.type === "WEB_FORM" && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenSnippet(conn)}
                            className="h-8 px-2 text-xs gap-1 text-primary hover:bg-primary/10"
                            title="Get the code to add the form to your website"
                          >
                            <Code className="size-3.5" />
                            <span>Form Code</span>
                          </Button>
                        )}

                        {conn.type === "INDIAMART" && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isSyncing}
                            onClick={() => handleSyncIndiaMart(conn)}
                            className="h-8 px-2 text-xs gap-1 text-success hover:bg-success/10"
                            title="Check IndiaMART for new leads now"
                          >
                            <RefreshCw className={cn("size-3.5", isSyncing && "animate-spin")} />
                            <span>Sync Now</span>
                          </Button>
                        )}

                        {(conn.type === "API_KEY" || conn.type === "WEBHOOK" || conn.type === "JUSTDIAL" || conn.type === "GOOGLE_ADS") && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const origin = window.location.origin;
                              setKeyReveal({
                                title: `${conn.name} Details`,
                                apiKey: conn.api_key_prefix ? `${conn.api_key_prefix}` : undefined,
                                webhookUrl:
                                  conn.type === "JUSTDIAL"
                                    ? `${origin}/api/Intake/JustDial/${conn.public_key}`
                                    : conn.type === "GOOGLE_ADS"
                                    ? `${origin}/api/Intake/Google/${conn.public_key}`
                                    : `${origin}/api/Intake/Hook/${conn.public_key}`,
                              });
                              setApiKeyModalOpen(true);
                            }}
                            className="h-8 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
                          >
                            <Key className="size-3.5" />
                            <span>Link</span>
                          </Button>
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={isTesting}
                          onClick={() => handleTestConnection(conn)}
                          className="h-8 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
                          title="Send simulated test lead"
                        >
                          <Play className={cn("size-3.5", isTesting && "animate-spin text-primary")} />
                          <span>Test Lead</span>
                        </Button>
                      </div>

                      <div className="flex items-center gap-0.5">
                        {conn.type === "API_KEY" && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setRotateKeyTarget(conn)}
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-warning"
                            title="Make a new secret code"
                          >
                            <RefreshCw className="size-3.5" />
                          </Button>
                        )}

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConnectionTarget(conn)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-danger"
                          title="Delete Connection"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: INQUIRY LOG (LEAD INBOX) */}
      {activeTab === "inbox" && (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by external ID, company, name, error..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-xs"
              />
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <SearchableSelect
                value={filterConnId}
                onChange={(e) => setFilterConnId(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-2xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">All Channels</option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </SearchableSelect>

              <SearchableSelect
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-2xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">All Statuses</option>
                <option value="RECEIVED">RECEIVED</option>
                <option value="PROCESSING">PROCESSING</option>
                <option value="CREATED">CREATED</option>
                <option value="MERGED">MERGED</option>
                <option value="FAILED">FAILED</option>
                <option value="SKIPPED">SKIPPED</option>
              </SearchableSelect>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => inboxList.reload()}
                className="h-9 px-2.5 text-xs"
                title="Refresh log"
              >
                <RefreshCw className={cn("size-3.5", inboxList.loading && "animate-spin")} />
              </Button>
            </div>
          </div>

          {/* Table Container */}
          <div className="bg-card border rounded-2xl overflow-hidden shadow-2xs">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-[140px] text-xs">Received</TableHead>
                  <TableHead className="text-xs">Channel</TableHead>
                  <TableHead className="text-xs">External ID / Name</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Result / Matched</TableHead>
                  <TableHead className="text-xs text-center">Attempts</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inboxList.loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-xs text-muted-foreground">
                      <RefreshCw className="size-4 animate-spin inline-block mr-2" />
                      Loading inquiry log...
                    </TableCell>
                  </TableRow>
                ) : inboxList.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-xs text-muted-foreground">
                      No inbound inquiries match the selected criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  inboxList.rows.map((item) => {
                    const statusColors: Record<string, string> = {
                      CREATED: "bg-success/10 text-success border-success/20",
                      MERGED: "bg-primary/10 text-primary border-primary/20",
                      RECEIVED: "bg-warning/10 text-warning border-warning/20",
                      PROCESSING: "bg-primary/10 text-accent-foreground border-primary/20",
                      FAILED: "bg-danger/10 text-danger border-danger/20",
                      SKIPPED: "bg-muted text-muted-foreground border-border",
                    };

                    const badgeClass = statusColors[item.inbox_status] || "bg-muted";

                    return (
                      <TableRow key={item.id} className="hover:bg-muted/40 transition-colors">
                        <TableCell className="text-xs text-muted-foreground font-mono">
                          {new Date(item.received_at).toLocaleString(dateLocale(), withClock({
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit" }))}
                        </TableCell>

                        <TableCell className="text-xs font-medium text-foreground">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate max-w-[140px]">{item.connection?.name || "Direct"}</span>
                          </div>
                        </TableCell>

                        <TableCell className="text-xs">
                          <div className="font-semibold text-foreground truncate max-w-[200px]">
                            {item.lead
                              ? `${item.lead.first_name} ${item.lead.last_name || ""}`.trim()
                              : item.external_id || "Anonymous"}
                          </div>
                          {item.lead?.company_name && (
                            <div className="text-[11px] text-muted-foreground truncate max-w-[200px]">
                              {item.lead.company_name}
                            </div>
                          )}
                        </TableCell>

                        <TableCell>
                          <Badge variant="outline" className={cn("text-[10px] font-semibold py-0.5", badgeClass)}>
                            {item.inbox_status}
                          </Badge>
                        </TableCell>

                        <TableCell className="text-xs">
                          {item.lead ? (
                            <Link
                              to={`/leads/${item.lead.id}`}
                              className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                            >
                              <span>View Lead</span>
                              <ExternalLink className="size-3" />
                            </Link>
                          ) : item.error ? (
                            <span className="text-danger truncate max-w-[200px] block" title={item.error}>
                              {item.error}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[11px]">
                              {item.match_reason || "Waiting"}
                            </span>
                          )}
                        </TableCell>

                        <TableCell className="text-xs text-center font-mono">
                          {item.attempts}
                        </TableCell>

                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewInboxDetail(item)}
                              className="h-7 px-2 text-xs"
                            >
                              Inspect
                            </Button>

                            {item.inbox_status === "FAILED" && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRetryInboxItem(item.id)}
                                className="h-7 px-2 text-xs text-warning hover:bg-warning/10"
                                title="Retry Processing"
                              >
                                <RefreshCw className="size-3" />
                              </Button>
                            )}

                            {item.inbox_status !== "SKIPPED" && item.inbox_status !== "CREATED" && item.inbox_status !== "MERGED" && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSkipInboxItem(item.id)}
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                                title="Skip item"
                              >
                                Skip
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            <ListPager
              page={inboxList.page}
              pageSize={inboxList.pageSize}
              total={inboxList.total}
              onPage={inboxList.setPage}
              disabled={inboxList.loading}
            />
          </div>
        </div>
      )}

      {/* DIALOG 1: CREATE CONNECTION WIZARD */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Connect External Lead Channel</DialogTitle>
            <DialogDescription>
              Set up a source that brings leads into the CRM, skips duplicates and assigns them to reps automatically.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateConnection} className="space-y-4 pt-2">
            {/* Channel Type Selector */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Select Channel Type</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(CONNECTION_TYPE_META).map(([typeKey, meta]) => {
                  const Icon = meta.icon;
                  const isSelected = formData.type === typeKey;
                  return (
                    <button
                      key={typeKey}
                      type="button"
                      onClick={() => {
                        const defaultSourceCode =
                          typeKey === "INDIAMART"
                            ? "INDIAMART"
                            : typeKey === "JUSTDIAL"
                            ? "JUSTDIAL"
                            : typeKey === "META_LEADS"
                            ? "FACEBOOK_ADS"
                            : typeKey === "GOOGLE_ADS"
                            ? "CAMPAIGN"
                            : "WEBSITE";
                        setFormData((p) => ({ ...p, type: typeKey, source_code: defaultSourceCode }));
                      }}
                      className={cn(
                        "p-2.5 rounded-xl border text-left flex flex-col justify-between transition-all",
                        isSelected
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border/80 hover:border-border hover:bg-muted/30"
                      )}
                    >
                      <div className={cn("size-7 rounded-lg flex items-center justify-center mb-2", meta.color)}>
                        <Icon className="size-3.5" />
                      </div>
                      <div>
                        <div className="font-bold text-xs text-foreground">{meta.label}</div>
                        <div className="text-[10px] text-muted-foreground line-clamp-1">{meta.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Basic details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Connection Name</Label>
                <Input
                  required
                  placeholder="e.g. Landing Page Contact Form"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  className="h-9 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Attributed Lead Source</Label>
                <SearchableSelect
                  value={formData.source_code}
                  onChange={(e) => setFormData((p) => ({ ...p, source_code: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-2xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {leadSources.map((ls) => (
                    <option key={ls.code} value={ls.code}>
                      {ls.name} ({ls.code})
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>

            {/* Type-Specific Options */}
            {formData.type === "WEB_FORM" && (
              <div className="bg-muted/30 border rounded-xl p-3 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Allowed Websites</Label>
                  <textarea
                    rows={2}
                    placeholder="https://example.com&#10;https://www.example.com (one per line, leave empty to allow any website)"
                    value={formData.allowed_origins}
                    onChange={(e) => setFormData((p) => ({ ...p, allowed_origins: e.target.value }))}
                    className="w-full text-xs font-mono rounded-md border border-input bg-background p-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Stops other websites from sending leads through your form link.
                  </p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Thank-you page link (after the form is sent)</Label>
                  <Input
                    placeholder="https://example.com/thank-you"
                    value={formData.redirect_url}
                    onChange={(e) => setFormData((p) => ({ ...p, redirect_url: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            )}

            {formData.type === "INDIAMART" && (
              <div className="bg-muted/30 border rounded-xl p-3 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">IndiaMART CRM Key</Label>
                  <Input
                    required
                    placeholder="Enter your IndiaMART CRM key"
                    value={formData.glusr_crm_key}
                    onChange={(e) => setFormData((p) => ({ ...p, glusr_crm_key: e.target.value }))}
                    className="h-8 text-xs font-mono"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Found in your IndiaMART seller panel under Settings → CRM Integration.
                  </p>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <div>
                    <div className="text-xs font-semibold text-foreground">Check automatically</div>
                    <div className="text-[10px] text-muted-foreground">The CRM checks IndiaMART for new leads every 5 minutes</div>
                  </div>
                  <Switch
                    checked={formData.enable_polling}
                    onCheckedChange={(checked) => setFormData((p) => ({ ...p, enable_polling: checked }))}
                  />
                </div>
              </div>
            )}

            {formData.type === "WEBHOOK" && (
              <div className="bg-muted/30 border rounded-xl p-3 space-y-2">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Secret Code (Optional)</Label>
                  <Input
                    placeholder="A secret code shared with the sending app"
                    value={formData.secret}
                    onChange={(e) => setFormData((p) => ({ ...p, secret: e.target.value }))}
                    className="h-8 text-xs font-mono"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Leads that don't carry this code will be refused.
                  </p>
                </div>
              </div>
            )}

            {formData.type === "GOOGLE_ADS" && (
              <div className="bg-muted/30 border rounded-xl p-3 space-y-2">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Google Ads Form Key</Label>
                  <Input
                    placeholder="The key you entered in your Google Ads lead form setup"
                    value={formData.google_key}
                    onChange={(e) => setFormData((p) => ({ ...p, google_key: e.target.value }))}
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>
            )}

            {/* Rep Assignment Routing */}
            <div className="space-y-3 pt-2 border-t border-border/80">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Lead Assignment Routing</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, routing_mode: "ROUND_ROBIN" }))}
                    className={cn(
                      "p-2.5 rounded-xl border text-left transition-all",
                      formData.routing_mode === "ROUND_ROBIN"
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border/80 hover:bg-muted/30"
                    )}
                  >
                    <div className="font-bold text-xs text-foreground flex items-center gap-1.5">
                      <Shuffle className="size-3.5 text-primary" />
                      <span>Round-Robin Pool</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Distribute evenly across sales reps in rotation
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, routing_mode: "DEFAULT" }))}
                    className={cn(
                      "p-2.5 rounded-xl border text-left transition-all",
                      formData.routing_mode === "DEFAULT"
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-border/80 hover:bg-muted/30"
                    )}
                  >
                    <div className="font-bold text-xs text-foreground flex items-center gap-1.5">
                      <Users className="size-3.5 text-primary" />
                      <span>Fixed Sales Rep</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Assign all inquiries from this source to one person
                    </div>
                  </button>
                </div>
              </div>

              {formData.routing_mode === "DEFAULT" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Select Assignee</Label>
                  <SearchableSelect
                    value={formData.default_owner_id}
                    onChange={(e) => setFormData((p) => ({ ...p, default_owner_id: e.target.value }))}
                    className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-2xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <option value="">Leave Unassigned (Shared List)</option>
                    {assignableReps.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.first_name} {r.last_name || ""}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {formData.routing_mode === "ROUND_ROBIN" && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold">Participating Reps ({formData.round_robin_user_ids.length})</Label>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          round_robin_user_ids:
                            p.round_robin_user_ids.length === assignableReps.length
                              ? []
                              : assignableReps.map((r) => r.id),
                        }))
                      }
                      className="text-[10px] text-primary hover:underline"
                    >
                      {formData.round_robin_user_ids.length === assignableReps.length ? "Deselect All" : "Select All"}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 max-h-36 overflow-y-auto border rounded-xl p-2 bg-muted/20">
                    {assignableReps.map((r) => {
                      const selected = formData.round_robin_user_ids.includes(r.id);
                      return (
                        <label
                          key={r.id}
                          className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-muted/50 cursor-pointer text-xs"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFormData((p) => ({
                                ...p,
                                round_robin_user_ids: checked
                                  ? [...p.round_robin_user_ids, r.id]
                                  : p.round_robin_user_ids.filter((id) => id !== r.id),
                              }));
                            }}
                            className="rounded border-border text-primary focus:ring-primary size-3.5"
                          />
                          <span className="truncate">
                            {r.first_name} {r.last_name || ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="font-semibold shadow-sm">
                Save & Connect Channel
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIALOG 2: FORM EMBED SNIPPET */}
      <Dialog open={snippetModalOpen} onOpenChange={setSnippetModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Website Form Code</DialogTitle>
            <DialogDescription>
              Copy and paste this code into your website or landing page, or send it to your web designer.
            </DialogDescription>
          </DialogHeader>

          {snippetLoading ? (
            <div className="py-12 text-center text-xs text-muted-foreground">
              <RefreshCw className="size-5 animate-spin mx-auto mb-2 text-primary" />
              Making the code...
            </div>
          ) : snippetData ? (
            <div className="space-y-4 pt-1">
              {/* Endpoint URL */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Website Form Link</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={snippetData.endpoint} className="font-mono text-xs h-8 bg-muted/40" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(snippetData.endpoint, "endpoint")}
                    className="h-8 gap-1.5 shrink-0"
                  >
                    {copiedKey === "endpoint" ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
                    <span>{copiedKey === "endpoint" ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
              </div>

              {/* HTML Snippet */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Ready-made Form</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(snippetData.html, "html")}
                    className="h-7 px-2 text-xs gap-1 text-primary"
                  >
                    {copiedKey === "html" ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
                    <span>{copiedKey === "html" ? "Copied" : "Copy Code"}</span>
                  </Button>
                </div>
                <pre className="bg-muted/60 p-3 rounded-xl border text-[11px] font-mono overflow-x-auto max-h-48 text-foreground/90">
                  {snippetData.html}
                </pre>
              </div>

              {/* JS Fetch Snippet */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Code for Your Own Form</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(snippetData.script, "script")}
                    className="h-7 px-2 text-xs gap-1 text-primary"
                  >
                    {copiedKey === "script" ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
                    <span>{copiedKey === "script" ? "Copied" : "Copy Code"}</span>
                  </Button>
                </div>
                <pre className="bg-muted/60 p-3 rounded-xl border text-[11px] font-mono overflow-x-auto max-h-48 text-foreground/90">
                  {snippetData.script}
                </pre>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" size="sm" onClick={() => setSnippetModalOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG 3: API KEY & ENDPOINT REVEAL */}
      <Dialog open={apiKeyModalOpen} onOpenChange={setApiKeyModalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{keyReveal?.title || "Channel Setup Details"}</DialogTitle>
            <DialogDescription>
              Copy these links and codes into the app that sends you leads.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 pt-1">
            {keyReveal?.apiKey && (
              <div className="bg-warning/10 border border-warning/30 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-warning">
                  <ShieldAlert className="size-4 shrink-0" />
                  <span>Important: Copy Your Secret Code Now</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  For safety, the full secret code will not be shown again.
                </p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={keyReveal.apiKey} className="font-mono text-xs h-8 bg-card" />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => handleCopy(keyReveal.apiKey!, "apiKey")}
                    className="h-8 gap-1 shrink-0 font-semibold"
                  >
                    {copiedKey === "apiKey" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                    <span>{copiedKey === "apiKey" ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
              </div>
            )}

            {keyReveal?.webhookUrl && (
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Lead Link</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={keyReveal.webhookUrl} className="font-mono text-xs h-8 bg-muted/40" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(keyReveal.webhookUrl, "webhookUrl")}
                    className="h-8 gap-1 shrink-0"
                  >
                    {copiedKey === "webhookUrl" ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
                    <span>{copiedKey === "webhookUrl" ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
              </div>
            )}

            {keyReveal?.secret && (
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Secret Code for Checking</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={keyReveal.secret} className="font-mono text-xs h-8 bg-muted/40" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(keyReveal.secret!, "secret")}
                    className="h-8 gap-1 shrink-0"
                  >
                    {copiedKey === "secret" ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
                    <span>{copiedKey === "secret" ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" size="sm" onClick={() => setApiKeyModalOpen(false)}>
              I Have Saved These Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG 4: INBOX ITEM DETAIL INSPECTOR */}
      <Dialog open={itemDetailModalOpen} onOpenChange={setItemDetailModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Inbound Inquiry Inspector</DialogTitle>
            <DialogDescription>
              See what was received, the cleaned-up details, how it matched existing leads, and any errors.
            </DialogDescription>
          </DialogHeader>

          {loadingItemDetail || !activeInboxItem ? (
            <div className="py-12 text-center text-xs text-muted-foreground">
              <RefreshCw className="size-5 animate-spin mx-auto mb-2 text-primary" />
              Loading item details...
            </div>
          ) : (
            <div className="space-y-4 pt-1">
              {/* Top metadata strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-muted/30 border rounded-xl p-3 text-xs">
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase font-semibold">Status</div>
                  <div className="font-bold text-foreground mt-0.5">{activeInboxItem.inbox_status}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase font-semibold">Attempts</div>
                  <div className="font-mono text-foreground mt-0.5">{activeInboxItem.attempts}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase font-semibold">External ID</div>
                  <div className="font-mono text-foreground mt-0.5 truncate">{activeInboxItem.external_id || "N/A"}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase font-semibold">Received At</div>
                  <div className="text-foreground mt-0.5">
                    {new Date(activeInboxItem.received_at).toLocaleTimeString(dateLocale(), withClock({
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit" }))}
                  </div>
                </div>
              </div>

              {/* Matched Lead / Error Notice */}
              {activeInboxItem.error && (
                <div className="bg-danger/10 border border-danger/30 rounded-xl p-3 space-y-1 text-xs text-danger">
                  <div className="font-bold flex items-center gap-1.5">
                    <AlertCircle className="size-4 shrink-0" />
                    <span>Processing Failure</span>
                  </div>
                  <p className="font-mono text-[11px] whitespace-pre-wrap">{activeInboxItem.error}</p>
                </div>
              )}

              {activeInboxItem.lead && (
                <div className="bg-success/10 border border-success/30 rounded-xl p-3 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-bold text-success flex items-center gap-1.5">
                      <CheckCircle2 className="size-4 shrink-0" />
                      <span>{activeInboxItem.inbox_status === "MERGED" ? "Merged into Existing Lead" : "Created New Lead"}</span>
                    </div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">
                      {activeInboxItem.lead.first_name} {activeInboxItem.lead.last_name || ""} • {activeInboxItem.match_reason || "Direct ingestion"}
                    </div>
                  </div>
                  <Link
                    to={`/leads/${activeInboxItem.lead.id}`}
                    className="inline-flex items-center gap-1 bg-success text-white font-semibold px-3 py-1.5 rounded-lg text-xs hover:bg-success/90 transition-colors shadow-2xs"
                  >
                    <span>Open Lead</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              )}

              {/* Normalized Data Preview */}
              {activeInboxItem.normalized && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Cleaned-up Lead Details</Label>
                  <div className="bg-muted/40 border rounded-xl p-3 text-xs space-y-1.5">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-muted-foreground">Name: </span>
                        <span className="font-semibold text-foreground">
                          {activeInboxItem.normalized.first_name} {activeInboxItem.normalized.last_name || ""}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Company: </span>
                        <span className="font-semibold text-foreground">{activeInboxItem.normalized.company_name || "-"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Phone: </span>
                        <span className="font-semibold text-foreground font-mono">{activeInboxItem.normalized.phone || "-"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Email: </span>
                        <span className="font-semibold text-foreground">{activeInboxItem.normalized.email || "-"}</span>
                      </div>
                    </div>
                    {activeInboxItem.normalized.requirement_notes && (
                      <div className="pt-1 border-t text-[11px] text-muted-foreground">
                        <span className="font-semibold text-foreground">Requirement: </span>
                        {activeInboxItem.normalized.requirement_notes}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Raw JSON Payload */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Everything Received</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(JSON.stringify(activeInboxItem.payload, null, 2), "rawPayload")}
                    className="h-7 px-2 text-xs gap-1 text-primary"
                  >
                    {copiedKey === "rawPayload" ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
                    <span>{copiedKey === "rawPayload" ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
                <pre className="bg-muted/70 p-3 rounded-xl border text-[11px] font-mono overflow-x-auto max-h-56 text-foreground">
                  {JSON.stringify(activeInboxItem.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}

          <DialogFooter className="pt-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {activeInboxItem && activeInboxItem.inbox_status === "FAILED" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleRetryInboxItem(activeInboxItem.id)}
                  className="gap-1.5 text-warning border-warning/30 hover:bg-warning/10"
                >
                  <RefreshCw className="size-3.5" />
                  <span>Retry Processing</span>
                </Button>
              )}
            </div>
            <Button type="button" size="sm" onClick={() => setItemDetailModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Connection Confirmation Dialog */}
      <Dialog
        open={!!deleteConnectionTarget}
        onOpenChange={(open) => !open && !deletingConnection && setDeleteConnectionTarget(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-danger-soft flex items-center justify-center text-danger shrink-0 mt-0.5">
                <Trash2 className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-base sm:text-lg text-danger">
                  Delete Intake Connection
                </DialogTitle>
                <DialogDescription className="text-xs sm:text-sm">
                  Are you sure you want to delete <strong className="text-foreground">{(deleteConnectionTarget || activeDeleteTarget.current)?.name}</strong>?
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-2 text-xs sm:text-sm text-muted-foreground bg-muted/40 p-3.5 rounded-xl border space-y-1.5">
            <p className="font-medium text-foreground text-xs">What happens when deleted:</p>
            <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
              <li>Historical leads and inquiry logs already in the CRM will remain intact.</li>
              <li>New inquiries sent to this channel's link will be refused straight away.</li>
            </ul>
          </div>

          <DialogFooter className="pt-2 flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDeleteConnectionTarget(null)}
              disabled={deletingConnection}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleConfirmDeleteConnection}
              disabled={deletingConnection}
              className="gap-1.5"
            >
              {deletingConnection ? (
                <>
                  <RefreshCw className="size-3.5 animate-spin" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <Trash2 className="size-3.5" />
                  <span>Delete Connection</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate Key Confirmation Dialog */}
      <Dialog
        open={!!rotateKeyTarget}
        onOpenChange={(open) => !open && !rotatingKey && setRotateKeyTarget(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-warning-soft flex items-center justify-center text-warning shrink-0 mt-0.5">
                <RefreshCw className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-base sm:text-lg text-warning">
                  Make New Secret Codes
                </DialogTitle>
                <DialogDescription className="text-xs sm:text-sm">
                  Make new secret codes for <strong className="text-foreground">{(rotateKeyTarget || activeRotateTarget.current)?.name}</strong>?
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-2 text-xs sm:text-sm bg-warning/10 border border-warning/20 text-warning p-3.5 rounded-xl space-y-1.5">
            <div className="flex items-center gap-1.5 font-semibold text-xs">
              <AlertCircle className="size-4 shrink-0 text-warning" />
              <span>Warning: Old Codes Stop Working</span>
            </div>
            <p className="text-xs leading-relaxed">
              The old secret code stops working straight away. Any app or form sending leads with the old code will fail until it is updated.
            </p>
          </div>

          <DialogFooter className="pt-2 flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRotateKeyTarget(null)}
              disabled={rotatingKey}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmRotateKey}
              disabled={rotatingKey}
              className="bg-warning hover:bg-warning/90 text-white gap-1.5"
            >
              {rotatingKey ? (
                <>
                  <RefreshCw className="size-3.5 animate-spin" />
                  <span>Making...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="size-3.5" />
                  <span>Make New Codes</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
